// The world art on the path, kept out of the startup bundle: the card strips
// and the Polaroids are lazy parts. Each slot holds its art's box while the
// chunk loads, and keeps it, plain, if the chunk cannot: the path never shifts
// and never shows a failure for a picture.
import type { Taken } from "@aybuben/client/device/polaroid-memory.ts";
import type { Stop } from "@aybuben/client/places/stops.ts";
import type { RegionAccent } from "@aybuben/client/world/palette.ts";
import type { LangId } from "@aybuben/core/lang.ts";
import { Suspense } from "react";

import { COL } from "../../theme.ts";
import { polaroidFrame } from "../home/polaroid-size.ts";
import { lazyPart, PartBoundary, usePartError } from "../lazy.tsx";

export const cardArtPart = lazyPart(() => import("./CardArt.tsx").then((m) => m.CardArt));
export const polaroidPart = lazyPart(() => import("../home/Polaroid.tsx").then((m) => m.Polaroid));

const CardBox = () => (
	<div aria-hidden style={{ width: "100%", aspectRatio: "340 / 110", background: COL.tint }} />
);

export function CardArtSlot(p: { placeId: string; accent: RegionAccent }) {
	const onError = usePartError("place-card-art");
	return (
		<PartBoundary fallback={<CardBox />} onError={onError}>
			<Suspense fallback={<CardBox />}>
				<cardArtPart.Part {...p} />
			</Suspense>
		</PartBoundary>
	);
}

interface PolaroidSlotProps {
	stop: Stop;
	taken: Taken | null;
	lang: LangId;
	size: number;
	tilt: number;
}

function PolaroidBox({ size, tilt }: { size: number; tilt: number }) {
	return (
		<div
			aria-hidden
			style={{
				width: size,
				height: polaroidFrame(size).height,
				// The paper's cream: it stays cream in either theme (see Polaroid).
				background: "#FFFDF8",
				transform: `rotate(${tilt}deg)`,
			}}
		/>
	);
}

export function PolaroidSlot(p: PolaroidSlotProps) {
	const onError = usePartError("place-polaroid");
	const box = <PolaroidBox size={p.size} tilt={p.tilt} />;
	return (
		<PartBoundary fallback={box} onError={onError}>
			<Suspense fallback={box}>
				<polaroidPart.Part {...p} />
			</Suspense>
		</PartBoundary>
	);
}
